import type { NovelSummary } from "../apiClient.js";

/**
 * "Continue reading": the novels this browser has an unfinished run in.
 *
 * The player saves the reader's place in localStorage under
 * `savedRuntimeKey(id)` (`lorevok:<id>:runtime`) every time they make a choice,
 * restart or leave an ending (PlayerPage's persistRuntime) — never from an
 * editor preview. Next to the position it stamps `progressStamp()`: when it was
 * saved and whether that place is an ending.
 *
 * A novel counts as in progress when the reader has made at least one choice
 * (more than the opening scene visited) and is not on an ending. So a finished
 * run stays off the shelf, and so does the reset a reader leaves behind when
 * they leave an ending ("To the novel" or "Read another story" save the opening
 * scene, so Play starts the story over). Progress saved before the stamp
 * existed cannot be told apart from a finished run and is left out.
 *
 * It is per browser and never leaves it, like the progress itself.
 */

/** What the player adds to every saved run (see PlayerPage persistRuntime). */
export interface ProgressStamp {
  /** When the run was saved (ms since epoch) — orders the shelf, newest first. */
  savedAt: number;
  /** True when the saved place is an ending scene: the run is finished. */
  atEnding: boolean;
}

export function progressStamp(atEnding: boolean, now: number = Date.now()): ProgressStamp {
  return { savedAt: now, atEnding };
}

export interface ReadingProgressEntry {
  novelId: string;
  savedAt: number;
  /** Scenes the reader's route has passed through, the current one included. */
  step: number;
}

/** How many novels the shelf shows. */
export const CONTINUE_READING_LIMIT = 4;

const RUNTIME_KEY = /^lorevok:(.+):runtime$/;

function storageKeys(storage: Storage): string[] {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key !== null) keys.push(key);
  }
  return keys;
}

function parseEntry(novelId: string, raw: string | null): ReadingProgressEntry | null {
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as Record<string, unknown> | null;
    if (!saved || typeof saved !== "object") return null;
    const { savedAt, atEnding, visitedSceneIds } = saved;
    if (typeof savedAt !== "number" || !Number.isFinite(savedAt)) return null;
    if (atEnding !== false) return null;
    if (!Array.isArray(visitedSceneIds) || visitedSceneIds.length < 2) return null;
    return { novelId, savedAt, step: visitedSceneIds.length };
  } catch {
    return null;
  }
}

/**
 * Every unfinished run saved in this browser, newest first. Storage that is
 * unavailable (private mode, blocked site data) reads as no runs.
 */
export function listReadingProgress(storage?: Storage): ReadingProgressEntry[] {
  try {
    const store = storage ?? window.localStorage;
    const entries: ReadingProgressEntry[] = [];
    for (const key of storageKeys(store)) {
      const match = RUNTIME_KEY.exec(key);
      if (!match) continue;
      const entry = parseEntry(match[1]!, store.getItem(key));
      if (entry) entries.push(entry);
    }
    return entries.sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

/**
 * The shelf: unfinished runs whose novel is in `novels` (the lists the app
 * already has — the public catalog), newest first, at most `limit`. A run whose
 * novel is not in the list (unpublished since, or further down the catalog
 * than the app has loaded) is skipped rather than fetched.
 */
export function pickContinueReading(
  novels: readonly NovelSummary[],
  entries: readonly ReadingProgressEntry[] = listReadingProgress(),
  limit: number = CONTINUE_READING_LIMIT
): { novel: NovelSummary; step: number }[] {
  const byId = new Map(novels.map((novel) => [novel.id, novel]));
  const shelf: { novel: NovelSummary; step: number }[] = [];
  for (const entry of entries) {
    if (shelf.length >= limit) break;
    const novel = byId.get(entry.novelId);
    if (novel) shelf.push({ novel, step: entry.step });
  }
  return shelf;
}
